import { createSlice } from '@reduxjs/toolkit';

import {
    createUnitFloorRoomMapping,
    fetchHospitalUnitOptions,
    fetchUnitFloorRoomMappings,
    updateUnitFloorRoomMapping
} from './unit-floor-room-mapping-api';
import type { HospitalUnitOption, UnitFloorRoomMapping } from '@/types/unit-floor-room-mapping';
import { DEFAULT_ROWS_PER_PAGE } from '@/constant';
import { logout } from '../auth/logout';
export interface UnitFloorRoomMappingState {
    mappings: UnitFloorRoomMapping[];
    units: HospitalUnitOption[];
    total: number;
    page: number;
    limit: number;
    mappingsLoading: boolean;
    mappingsError: string | null;
    unitsLoading: boolean;
    unitsError: string | null;
    createMappingLoading: boolean;
    createMappingError: string | null;
    updateMappingLoading: boolean;
    updateMappingError: string | null;
}

const initialState: UnitFloorRoomMappingState = {
    mappings: [],
    units: [],
    total: 0,
    page: 1,
    limit: DEFAULT_ROWS_PER_PAGE,
    mappingsLoading: false,
    mappingsError: null,
    unitsLoading: false,
    unitsError: null,
    createMappingLoading: false,
    createMappingError: null,
    updateMappingLoading: false,
    updateMappingError: null
};

const unitFloorRoomMappingSlice = createSlice({
    name: 'unitFloorRoomMapping',
    initialState,
    reducers: {
        clearUnitFloorRoomMappingErrors: (state) => {
            state.mappingsError = null;
            state.unitsError = null;
            state.createMappingError = null;
            state.updateMappingError = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchHospitalUnitOptions.pending, (state) => {
                state.unitsLoading = true;
                state.unitsError = null;
            })
            .addCase(fetchHospitalUnitOptions.fulfilled, (state, action) => {
                state.unitsLoading = false;
                state.units = action.payload;
                state.unitsError = null;
            })
            .addCase(fetchHospitalUnitOptions.rejected, (state, action) => {
                state.unitsLoading = false;
                state.unitsError = action.payload ?? 'Failed to fetch units';
            })
            .addCase(fetchUnitFloorRoomMappings.pending, (state) => {
                state.mappingsLoading = true;
                state.mappingsError = null;
            })
            .addCase(fetchUnitFloorRoomMappings.fulfilled, (state, action) => {
                state.mappingsLoading = false;
                state.mappings = action.payload.items;
                state.total = action.payload.total;
                state.page = action.payload.page;
                state.limit = action.payload.limit;
                state.mappingsError = null;
            })
            .addCase(fetchUnitFloorRoomMappings.rejected, (state, action) => {
                state.mappingsLoading = false;
                state.mappingsError = action.payload ?? 'Failed to fetch mappings';
            })
            .addCase(createUnitFloorRoomMapping.pending, (state) => {
                state.createMappingLoading = true;
                state.createMappingError = null;
            })
            .addCase(createUnitFloorRoomMapping.fulfilled, (state, action) => {
                state.createMappingLoading = false;
                state.createMappingError = null;
                state.mappings = [action.payload, ...state.mappings];
                state.total += 1;
            })
            .addCase(createUnitFloorRoomMapping.rejected, (state, action) => {
                state.createMappingLoading = false;
                state.createMappingError = action.payload ?? 'Failed to create mapping';
            })
            .addCase(updateUnitFloorRoomMapping.pending, (state) => {
                state.updateMappingLoading = true;
                state.updateMappingError = null;
            })
            .addCase(updateUnitFloorRoomMapping.fulfilled, (state, action) => {
                state.updateMappingLoading = false;
                state.updateMappingError = null;
                state.mappings = state.mappings.map((mapping) =>
                    mapping.id === action.payload.id ? { ...mapping, ...action.payload } : mapping
                );
            })
            .addCase(updateUnitFloorRoomMapping.rejected, (state, action) => {
                state.updateMappingLoading = false;
                state.updateMappingError = action.payload ?? 'Failed to update mapping';
            })
            .addCase(logout, () => initialState);
    }
});

export const { clearUnitFloorRoomMappingErrors } = unitFloorRoomMappingSlice.actions;
export default unitFloorRoomMappingSlice.reducer;
